import React, { useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import { PapiDeviation } from '@wix/da-papi-types';
import { SessionContext } from '@wix/da-react-context/pkg/publicSession';
import MainToggle from '@wix/da-ds/pkg/formControls/dsPresets/MainToggle';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import InfoIcon from '@wix/da-ds/pkg/Icons/16x16/Info';
import SimpleTooltip from '@wix/da-ds/pkg/tooltips/dsPreset/SimpleTooltip';
import ButtonFieldLayout from '../../layouts/common/ButtonFieldLayout';
import { useCoreMonetizationUpsellPopup } from '../../modals/CoreMonetizationUpsellPopup/useCoreMonetizationUpsellPopup';
import { useSubmitField } from '../../hooks/useSubmitField';
import { initAdapter } from './adapters';

import { sanitizeHref } from '@wix/da-url';

import s from './PcpLabelWithToggleField.scss';

interface Props {
  deviation: PapiDeviation;
  inTier?: boolean;
}

const PcpLabelWithToggleField: React.FC<Props> = ({ deviation, inTier }) => {
  const { hasCoreMembership } = useContext(SessionContext);
  const { setValue, watch } = useFormContext();
  const { t: translateFn } = useTranslation();
  const t = (key: string, ...args: any[]) =>
    (translateFn as (key: string, ...args: any[]) => ReturnType<typeof translateFn>)(
      `deviationSubmit.form.attachments.${key}`,
      ...args
    );

  const {
    id,
    field: { onChange: onToggleChange, value },
    ref,
  } = useSubmitField('showAttachments', {
    initAdapter,
  });

  const sellExclusiveToggle = watch('sellExclusiveToggle');
  const originalAttached = watch('originalAttached');

  const { showCoreUpsellPopup, coreUpsellPopup } = useCoreMonetizationUpsellPopup();

  const label = (
    <span className={s['label']}>
      {t('label')}
      <SimpleTooltip
        text={t('tooltip', {
          postProcess: ['reactPostprocessor'],
          learnMoreLink: (
            <a
              href={sanitizeHref(t('tooltip.url'))}
              rel="license noopener noreferrer"
              target="_blank"
            >
              {t('tooltip.link')}
            </a>
          ),
        })}
        stopPropagation={true}
      >
        <InfoIcon className={s['tooltip-icon']} size={IconSize.SMALLER} disableDefaultColors />
      </SimpleTooltip>
    </span>
  );
  const canSubmitPcp = hasCoreMembership || value;

  const handleToggleChange = useCallback(
    newValue => {
      if (!canSubmitPcp) {
        showCoreUpsellPopup();
        return;
      }
      if (newValue && originalAttached) {
        setValue('allowFreeDownload', false, { shouldDirty: true });
      }
      onToggleChange(newValue);
    },
    [canSubmitPcp, showCoreUpsellPopup, onToggleChange, originalAttached, setValue]
  );

  // If this form has Exclusive on, or the deviation is already an adoptable,
  // or it has exclusive assets that have been uploaded alongside the original,
  // PCP isn't an allowable state for the deviation
  const toggleIsDisabled = Boolean(
    sellExclusiveToggle ||
    deviation?.isAdoptable ||
    (deviation?.extended?.adoptable?.assets || []).filter(asset => !asset.isOriginal).length > 0
  );
  const toggle = (
    <MainToggle
      variant="main"
      id={id}
      size={'small'}
      value={value}
      onChange={handleToggleChange}
      aria-labelledby={`${id}-label`}
      disabled={toggleIsDisabled}
    />
  );

  return (
    <ButtonFieldLayout
      label={label}
      labelProps={{ id: `${id}-label`, htmlFor: id }}
      ref={el => {
        ref.current = el;
      }}
    >
      {toggleIsDisabled ? (
        <SimpleTooltip
          text={
            sellExclusiveToggle
              ? t('disabled.tooltip.exclusiveToggleOn')
              : t('disabled.tooltip.isAdoptable')
          }
          usePortal
        >
          {toggle}
        </SimpleTooltip>
      ) : (
        toggle
      )}
      {coreUpsellPopup}
    </ButtonFieldLayout>
  );
};
PcpLabelWithToggleField.displayName = 'PcpLabelWithToggleField';

export default PcpLabelWithToggleField;
